import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

/**
 * Display-formatter for `Part.mounting`. Storage stays lowercase (the
 * `Mounting` enum's wire value), but acronyms render uppercase
 * everywhere a user sees them — "SMD" / "THT" / "Chassis" / "Other".
 *
 * Centralized here so the parts list, drawer, validate modal, and any
 * future surface stay consistent. Returns `'—'` for null/empty so
 * callers can drop their own dash fallback.
 */
export function formatMounting(value: string | null | undefined): string {
  if (!value) return '—';
  const map: Record<string, string> = {
    smd: 'SMD',
    tht: 'THT',
    chassis: 'Chassis',
    other: 'Other',
  };
  return map[value.toLowerCase()] ?? value;
}

export interface InventoryOut {
  quantity_on_hand: number;
  quantity_is_approximate: boolean;
  quantity_threshold: number;
  location_id: number | null;
  form: string | null;
  last_counted_at: string | null;
}

export interface PartListItem {
  id: number;
  part_number: string;
  mfg_pn: string | null;
  alt_mfg_pn?: string | null;
  manufacturer: string | null;
  alt_manufacturer?: string | null;
  description: string | null;
  category_id: number | null;
  subcategory?: string | null;
  package: string | null;
  mounting: string | null;
  lifecycle: string;
  rohs_compliant: boolean | null;
  kicad_tracked: boolean;
  kicad_has_3d_model: boolean;
  inventory: InventoryOut | null;
  row_version: number;
  updated_at: string;

  // Spec fields — null on most parts, populated for the part types
  // where they apply. Surfaced on the list response so the column
  // picker can offer them without forcing a per-row detail fetch.
  value?: string | null;
  tolerance?: string | null;
  dielectric?: string | null;
  polarity?: string | null;
  color?: string | null;
  voltage_v?: number | null;
  voltage_v_max?: number | null;
  current_a?: number | null;
  power_w?: number | null;
  vf?: number | null;
  vr?: number | null;
  voltage_clamp_v?: number | null;
  frequency_hz?: number | null;
  wavelength_nm?: number | null;
  pin_count?: number | null;
  pitch_mm?: number | null;
  temp_min_c?: number | null;
  temp_max_c?: number | null;
  output_voltage_v?: number | null;
  output_voltage_v_max?: number | null;
  output_current_a?: number | null;
  regulator_output_type?: string | null;
  hold_current_a?: number | null;
  trip_current_a?: number | null;
  // 0014 spec columns.
  luminous_intensity_mcd?: number | null;
  load_capacitance_pf?: number | null;
  rds_on_ohms?: number | null;
  hfe?: number | null;
  // Flattened primary-supplier (priority=1) info — populated by the
  // list endpoint so the parts table can show "where this came from"
  // without per-row sub-fetches. Null when the part has no suppliers.
  primary_supplier_name?: string | null;
  primary_supplier_slug?: string | null;
  primary_supplier_pn?: string | null;
  primary_supplier_url?: string | null;
  supplier_count?: number;
}

export interface PartDetail extends PartListItem {
  alt_mfg_pn: string | null;
  alt_manufacturer: string | null;
  subcategory: string | null;
  value: string | null;
  tolerance: string | null;
  dielectric: string | null;
  power_w: number | null;
  voltage_v: number | null;
  voltage_v_max: number | null;
  current_a: number | null;
  vf: number | null;
  vr: number | null;
  frequency_hz: number | null;
  temp_min_c: number | null;
  temp_max_c: number | null;
  polarized: boolean | null;
  pin_count: number | null;
  // Type-specific specs introduced by migration 0013. Each field
  // applies only to certain part categories — the UI shows whichever
  // is non-null. See `app/models/parts.py` for column-by-column rationale.
  polarity: string | null;
  output_voltage_v: number | null;
  output_voltage_v_max: number | null;
  output_current_a: number | null;
  regulator_output_type: string | null;
  voltage_clamp_v: number | null;
  hold_current_a: number | null;
  trip_current_a: number | null;
  color: string | null;
  wavelength_nm: number | null;
  pitch_mm: number | null;
  // 0014 spec columns.
  luminous_intensity_mcd: number | null;
  load_capacitance_pf: number | null;
  rds_on_ohms: number | null;
  hfe: number | null;
  datasheet_url: string | null;
  image_url: string | null;
  kicad_symbol: string | null;
  kicad_footprint: string | null;
  kicad_fields: Record<string, unknown> | null;
  external_refs: Record<string, unknown> | null;
  visibility: string;
  extra_fields: Record<string, unknown> | null;
  tags: string[] | null;
  notes: string | null;
}

export interface PartListResponse {
  items: PartListItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface PartsListParams {
  q?: string;
  category_id?: number;
  package?: string;
  mounting?: string;
  lifecycle?: string;
  /** Restrict to parts with at least one supplier matching this slug. */
  distributor_slug?: string;
  low_stock?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
}

function buildQuery(params: PartsListParams): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export function usePartsList(orgSlug: string | undefined, params: PartsListParams) {
  return useQuery<PartListResponse>({
    queryKey: ['parts', orgSlug, params],
    queryFn: () => api.get<PartListResponse>(`/api/orgs/${orgSlug}/parts${buildQuery(params)}`),
    enabled: Boolean(orgSlug),
    placeholderData: keepPreviousData,
  });
}

export function usePartDetail(orgSlug: string | undefined, partId: number | null) {
  return useQuery<PartDetail>({
    queryKey: ['part', orgSlug, partId],
    queryFn: () => api.get<PartDetail>(`/api/orgs/${orgSlug}/parts/${partId}`),
    enabled: Boolean(orgSlug && partId),
  });
}

export interface PartCategory {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null;
  sort_order: number;
  // Augmented fields from the per-org endpoint (added in 0.7.9). The
  // dropdown consumers don't read these but the management panel does;
  // keeping them on the shared type avoids a separate row interface.
  part_count?: number;
  child_count?: number;
}

export function usePartCategories(orgSlug: string | null | undefined) {
  return useQuery<PartCategory[]>({
    queryKey: ['part-categories', orgSlug],
    queryFn: () =>
      api.get<PartCategory[]>(`/api/orgs/${orgSlug}/part-categories`),
    enabled: Boolean(orgSlug),
    staleTime: 5 * 60_000,
  });
}

export interface CreatePartCategoryInput {
  name: string;
  parent_id?: number | null;
  sort_order?: number;
}

export function useCreatePartCategory(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePartCategoryInput) =>
      api.postJson<PartCategory>(
        `/api/orgs/${orgSlug}/part-categories`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['part-categories', orgSlug] });
    },
  });
}

export interface UpdatePartCategoryInput {
  id: number;
  name?: string;
  parent_id?: number | null;
  sort_order?: number;
  // Pydantic distinguishes "not sent" from "set to null" by checking
  // model_fields_set; mirror by only sending keys the caller wants
  // changed. The hook accepts the union and the caller's spread
  // controls what lands in the body.
}

export function useUpdatePartCategory(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdatePartCategoryInput) =>
      api.patchJson<PartCategory>(
        `/api/orgs/${orgSlug}/part-categories/${id}`,
        patch,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['part-categories', orgSlug] });
    },
  });
}

export interface DeletePartCategoryInput {
  id: number;
  reassign_children_to?: number | null;
  reassign_parts_to?: number | null;
}

export function useDeletePartCategory(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reassign_children_to, reassign_parts_to }: DeletePartCategoryInput) => {
      const params = new URLSearchParams();
      if (reassign_children_to != null) params.set('reassign_children_to', String(reassign_children_to));
      if (reassign_parts_to != null) params.set('reassign_parts_to', String(reassign_parts_to));
      const qs = params.toString();
      return api.delete<void>(
        `/api/orgs/${orgSlug}/part-categories/${id}${qs ? `?${qs}` : ''}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['part-categories', orgSlug] });
      qc.invalidateQueries({ queryKey: ['parts', orgSlug] });
    },
  });
}

export interface PartCreateInput {
  mfg_pn?: string;
  manufacturer?: string;
  alt_mfg_pn?: string;
  alt_manufacturer?: string;
  description?: string;
  category_id?: number | null;
  package?: string;
  mounting?: string;
  value?: string;
  tolerance?: string;
  dielectric?: string;
  voltage_v?: number | null;
  power_w?: number | null;
  vf?: number | null;
  vr?: number | null;
  // Type-specific specs (migration 0013).
  polarity?: string;
  output_voltage_v?: number | null;
  output_voltage_v_max?: number | null;
  output_current_a?: number | null;
  regulator_output_type?: string;
  voltage_clamp_v?: number | null;
  hold_current_a?: number | null;
  trip_current_a?: number | null;
  color?: string;
  wavelength_nm?: number | null;
  pitch_mm?: number | null;
  luminous_intensity_mcd?: number | null;
  load_capacitance_pf?: number | null;
  rds_on_ohms?: number | null;
  hfe?: number | null;
  rohs_compliant?: boolean | null;
  kicad_tracked?: boolean;
  kicad_symbol?: string;
  kicad_footprint?: string;
  kicad_has_3d_model?: boolean;
  lifecycle?: string;
  datasheet_url?: string;
  notes?: string;
}

export function useCreatePart(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      payload,
      allowDuplicate,
    }: {
      payload: PartCreateInput;
      allowDuplicate?: boolean;
    }) =>
      api.postJson<PartDetail>(
        `/api/orgs/${orgSlug}/parts${allowDuplicate ? '?allow_duplicate_mfg_pn=true' : ''}`,
        payload,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts', orgSlug] }),
  });
}

/**
 * PATCH payload shape. All fields are nullable so the UI can clear a
 * value (set to null) as well as update it. `row_version` carries the
 * client's last-seen version for optimistic locking — the server bumps
 * it on success and 409s if it doesn't match.
 */
export interface PartUpdateInput {
  row_version: number;
  part_number?: string | null;
  mfg_pn?: string | null;
  manufacturer?: string | null;
  alt_mfg_pn?: string | null;
  alt_manufacturer?: string | null;
  description?: string | null;
  category_id?: number | null;
  subcategory?: string | null;
  value?: string | null;
  tolerance?: string | null;
  dielectric?: string | null;
  voltage_v?: number | null;
  voltage_v_max?: number | null;
  power_w?: number | null;
  current_a?: number | null;
  vf?: number | null;
  vr?: number | null;
  frequency_hz?: number | null;
  temp_min_c?: number | null;
  temp_max_c?: number | null;
  package?: string | null;
  mounting?: string | null;
  polarized?: boolean | null;
  pin_count?: number | null;
  polarity?: string | null;
  output_voltage_v?: number | null;
  output_voltage_v_max?: number | null;
  output_current_a?: number | null;
  regulator_output_type?: string | null;
  voltage_clamp_v?: number | null;
  hold_current_a?: number | null;
  trip_current_a?: number | null;
  color?: string | null;
  wavelength_nm?: number | null;
  pitch_mm?: number | null;
  luminous_intensity_mcd?: number | null;
  load_capacitance_pf?: number | null;
  rds_on_ohms?: number | null;
  hfe?: number | null;
  rohs_compliant?: boolean | null;
  lifecycle?: string;
  datasheet_url?: string | null;
  image_url?: string | null;
  visibility?: string;
  kicad_tracked?: boolean;
  kicad_symbol?: string | null;
  kicad_footprint?: string | null;
  kicad_has_3d_model?: boolean;
  kicad_fields?: Record<string, unknown> | null;
  external_refs?: Record<string, unknown> | null;
  extra_fields?: Record<string, unknown> | null;
  tags?: string[] | null;
  notes?: string | null;
}

export function useUpdatePart(orgSlug: string | undefined, partId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartUpdateInput) =>
      api.patchJson<PartDetail>(
        `/api/orgs/${orgSlug}/parts/${partId}`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['part', orgSlug, partId] });
      qc.invalidateQueries({ queryKey: ['parts', orgSlug] });
    },
  });
}

export function useDeletePart(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partId: number) =>
      api.delete<unknown>(`/api/orgs/${orgSlug}/parts/${partId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts', orgSlug] }),
  });
}
